/**
 * Generador de archivos .ics (iCalendar) escrito a mano: un VEVENT son veinte
 * líneas de texto plano y no amerita una dependencia. Lo abren Google Calendar,
 * Apple Calendar y Outlook sin cuenta ni API.
 *
 * La hora va como tiempo flotante (sin zona): el teléfono del invitado la
 * interpreta en su hora local, que para una boda presencial es lo correcto.
 * Sin hora parseable, el evento es de día completo.
 */

export interface EventoCalendario {
  titulo: string
  descripcion: string
  lugar: string
  /** Fecha ISO (YYYY-MM-DD). Sin fecha válida no hay archivo. */
  fecha: string
  /** Texto libre; si trae un HH:MM parseable, el evento arranca a esa hora. */
  hora: string
  /** URL de la invitación, para volver desde el evento del calendario. */
  url: string
}

/** Comas, puntos y coma y saltos de línea van escapados por el estándar. */
function escaparTexto(texto: string): string {
  return texto
    .replaceAll('\\', '\\\\')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
    .replaceAll(/\r?\n/g, '\\n')
}

function aCompacto(fecha: string): string {
  return fecha.replaceAll('-', '')
}

/** El día siguiente en YYYYMMDD, para el DTEND de un evento de día completo. */
function diaSiguiente(fecha: string): string {
  const dia = new Date(`${fecha}T00:00:00Z`)
  dia.setUTCDate(dia.getUTCDate() + 1)
  return dia.toISOString().slice(0, 10).replaceAll('-', '')
}

const HORA = /(\d{1,2}):(\d{2})/

/** Devuelve el contenido del .ics, o null si la fecha no es una fecha. */
export function icsDeBoda(evento: EventoCalendario): string | null {
  const fecha = evento.fecha.trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return null
  if (Number.isNaN(new Date(`${fecha}T00:00:00Z`).getTime())) return null

  const coincidencia = HORA.exec(evento.hora)
  const horas = coincidencia ? Number(coincidencia[1]) : null
  const minutos = coincidencia ? Number(coincidencia[2]) : null
  const conHora =
    horas !== null && minutos !== null && horas >= 0 && horas <= 23 && minutos >= 0 && minutos <= 59

  const inicio = conHora
    ? `DTSTART:${aCompacto(fecha)}T${String(horas).padStart(2, '0')}${String(minutos).padStart(2, '0')}00`
    : `DTSTART;VALUE=DATE:${aCompacto(fecha)}`
  const fin = conHora ? null : `DTEND;VALUE=DATE:${diaSiguiente(fecha)}`

  const descripcion = [escaparTexto(evento.descripcion.trim()), evento.url.trim()]
    .filter((parte) => parte !== '')
    .join('\\n')

  const lineas = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Nuestra Boda//ES',
    'BEGIN:VEVENT',
    `UID:${aCompacto(fecha)}-boda@nuestra-boda`,
    `DTSTAMP:${new Date().toISOString().replaceAll(/[-:]/g, '').slice(0, 15)}Z`,
    inicio,
    fin,
    `SUMMARY:${escaparTexto(evento.titulo)}`,
    evento.lugar.trim() !== '' ? `LOCATION:${escaparTexto(evento.lugar)}` : null,
    descripcion !== '' ? `DESCRIPTION:${descripcion}` : null,
    evento.url.trim() !== '' ? `URL:${evento.url.trim()}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter((linea): linea is string => linea !== null)

  return lineas.join('\r\n')
}

/** Descarga el .ics en el navegador. No hace nada si el contenido es null. */
export function descargarIcs(nombreArchivo: string, contenido: string | null): void {
  if (contenido === null) return
  const blob = new Blob([contenido], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const ancla = document.createElement('a')
  ancla.href = url
  ancla.download = nombreArchivo
  document.body.appendChild(ancla)
  ancla.click()
  ancla.remove()
  URL.revokeObjectURL(url)
}
